import { css } from '@microsoft/fast-element';
import { neutralForegroundActive, neutralForegroundRest, neutralInputFillHover, neutralInputFillRest, neutralLayer1, neutralStealthFillActive, neutralStealthFillHover, neutralStealthFillRest, neutralStrokeFocus, neutralStrokeRest, getToken, neutralDivider, neutralForegroundHover } from '../../design-tokens';
import { appearanceBehavior } from '../../styles/behaviors';
import { elevationRule } from '../../styles/elevation';
import { heightNumber, paddingNumber } from '../../styles/size';
import { display } from '../../foundation/display';

export const selectStealthStyles = css`
        :host .control {
            background: ${neutralStealthFillRest};
            border-color: transparent;
            box-shadow: none;
        }

        :host(:not([disabled]):hover) .control {
            background: ${neutralStealthFillHover};
            border-color: transparent;
            box-shadow: none;
        }

        :host(:not([disabled]):active) .control {
            background: ${neutralStealthFillActive};
            border-color: transparent;
            box-shadow: none;
        }
    `;

export const selectLightweightStyles = css`
        :host .control {
            background: transparent;
            border-color: transparent;
            box-shadow: none;
        }

        :host(:not([disabled]):hover) .control {
            background: ${neutralStealthFillHover};
            border-color: transparent;
            box-shadow: none;
        }

        :host(:not([disabled]):active) .control {
            background: ${neutralStealthFillActive};
            border-color: transparent;
            box-shadow: none;
        }
    `;

export const selectStyles = css`
    ${display("inline-flex")} :host {
        --elevation: ${getToken('space-high-elevation')};
        --listbox-max-height: 6;
        --listbox-available-height: 100px;
        position: relative;
        box-sizing: border-box;

        font-family: ${getToken('typography-font-family')};
        fill: currentcolor;

        user-select: none;
        outline: none;
    }

    :host .listbox {
        ${elevationRule}
        background: ${neutralLayer1};
        border: calc(${getToken('space-stroke-width')} * 1px) solid ${neutralStrokeRest};
        border-radius: calc(${getToken('space-input-corner-radius')} * 1px);
        box-sizing: border-box;
        display: inline-flex;
        flex-direction: column;
        max-height: min(var(--listbox-available-height), calc((var(--listbox-max-height) * ${heightNumber} + ${getToken('space-stroke-width')}) * 1px));
        padding: 0;
        overflow-y: auto;
        z-index: 10;
    }

    :host([multiple]) .listbox,
    :host([no-collapse]) .listbox {
        width: 100%;
        height: calc((var(--listbox-max-height) * ${heightNumber} + ${getToken('space-stroke-width')}) * 1px);
        max-height: none;
    }

    :host .listbox[hidden] {
        display: none;
    }

    :host .control {
        border: calc(${getToken('space-stroke-width')} * 1px) solid ${neutralStrokeRest};
        border-radius: calc(${getToken('space-input-corner-radius')} * 1px);
        height: calc(${heightNumber} * 1px);
        vertical-align: top;
        background: ${neutralInputFillRest};
        color: ${neutralForegroundRest};
        box-shadow: 0 0 calc(${getToken('space-input-shadow')} * 1px) 0 rgba(0, 0, 0, 0.1);
        align-items: center;
        box-sizing: border-box;
        cursor: pointer;
        display: flex;
        font-size: ${getToken('typography-control-font-size')};
        font-family: inherit;
        line-height: ${getToken('typography-control-line-height')};
        padding-left: calc(${paddingNumber} * 1px);
        min-width: 100%;
    }

    :host(.label__visible) .control {
        height: calc(${heightNumber} * 1px + ${getToken('typography-body-x-small-font-size')});
    }

    :host(.label__visible) .selected-value {
        padding-top: calc(${getToken('typography-body-x-small-line-height')} * ${getToken('typography-body-x-small-font-size')});
    }

    :host(:not([disabled]):hover) .control {
        background: ${neutralInputFillHover};
        border-color: ${getToken('color-primary-hover')};
    }

    :host(:focus-visible) .control {
        border-color: ${neutralStrokeFocus};
        box-shadow: 0 0 0 calc(${getToken('space-focus-stroke-width')} * 1px) ${neutralStrokeFocus};
    }

    :host([disabled]) {
        cursor: not-allowed;
        opacity: ${getToken('color-disabled-opacity')};
    }

    :host([disabled]) .control {
        cursor: not-allowed;
        user-select: none;
    }

    :host([open]) .control {
        color: ${neutralForegroundActive};
    }

    .label {
        position: absolute;
        top: calc(50% - ${getToken('typography-control-line-height')} * ${getToken('typography-control-font-size')} / 2);

        display: block;

        padding-left: calc((${paddingNumber} + ${getToken('space-stroke-width')}) * 1px);

        color: ${neutralForegroundRest};

        user-select: none;
        cursor: text;

        transition: 200ms ease;
    }

    :host(.label__hidden) .label {
        display: none;
        visibility: hidden;
    }

    .control:focus + .label,
    :host([open]) .control + .label,
    :host(:not([current-value=""])) .control + .label,
    :host([placeholder]) .control + .label
     {
        top: calc(${getToken('space-design-unit')} * 1px);
        font-size: ${getToken('typography-body-x-small-font-size')};
        line-height: ${getToken('typography-body-x-small-line-height')};
    }

    :host(.label__visible[no-collapse]) .label {
        display: block;
        position: static;
        padding-left: 1ch;
        color: ${neutralForegroundHover};
    }

    :host([open][position="above"]) .listbox {
        bottom: calc((${heightNumber} + ${getToken('space-design-unit')}) * 1px);
    }

    :host([open][position="below"]) .listbox {
        top: calc((${heightNumber} + ${getToken('space-design-unit')}) * 1px);
    }

    .selected-value {
        flex: 1 1 auto;
        font-family: inherit;
        text-align: start;
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
    }

    .indicator {
        display: flex;
        justify-content: center;
        flex: 0 0 auto;
        width: calc(${heightNumber} * 1px);
    }

    slot[name="listbox"] {
        display: none;
        min-width: 100%;
    }

    :host([open]) slot[name="listbox"] {
        display: flex;
        position: absolute;
        ${elevationRule}
    }

    .end {
        margin-inline-start: auto;
    }

    ::slotted(svg),
    slot > svg {
        display: flex;
        height: 1em;
        width: 1em;
    }

    ::slotted([role="option"]) {
        flex: 0 0 auto;
    }

    ::slotted([role="option"]:not(:last-child)) {
        border-bottom: calc(${getToken('space-stroke-width')} * 1px) solid ${neutralDivider} !important;
    }
`.withBehaviors(
    appearanceBehavior("stealth", selectStealthStyles),
    appearanceBehavior("lightweight", selectLightweightStyles)
);
